import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { initGA } from '@/lib/analytics';
import {
  OPEN_COOKIE_SETTINGS_EVENT,
  hasAnalyticsConsent,
  readConsent,
  saveConsent,
} from '@/lib/consent';

/** Brand cookie: anthracite cookie with a yellow bite. */
const CookieMark = ({ animate }: { animate: boolean }) => (
  <motion.svg
    viewBox="0 0 48 48"
    className="w-11 h-11 shrink-0"
    aria-hidden="true"
    initial={animate ? { rotate: -18, scale: 0.85 } : false}
    animate={{ rotate: 0, scale: 1 }}
    transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.15 }}
  >
    <defs>
      <mask id="cookie-bite">
        <rect width="48" height="48" fill="white" />
        <circle cx="41" cy="9" r="8" fill="black" />
        <circle cx="45" cy="19" r="5" fill="black" />
      </mask>
    </defs>
    <circle cx="24" cy="24" r="20" fill="#FFC300" mask="url(#cookie-bite)" />
    <g fill="#2A3A4A">
      <circle cx="16" cy="18" r="2.6" />
      <circle cx="27" cy="29" r="2.9" />
      <circle cx="15" cy="31" r="2.2" />
      <circle cx="30" cy="16" r="1.8" />
      <circle cx="35" cy="31" r="1.6" />
    </g>
  </motion.svg>
);

const Switch = ({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC300] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2A3A4A]
      ${checked ? 'bg-[#FFC300]' : 'bg-white/20'} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const CookieBanner: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  // First visit: show after a short delay. Footer link can reopen it any time.
  useEffect(() => {
    const saved = readConsent();
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (saved) {
      setAnalytics(saved.analytics);
    } else {
      timer = setTimeout(() => setIsVisible(true), 800);
    }
    const open = () => {
      setAnalytics(hasAnalyticsConsent());
      setShowSettings(true);
      setIsVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    };
  }, []);

  const decide = (allowAnalytics: boolean) => {
    const hadAnalytics = hasAnalyticsConsent();
    saveConsent(allowAnalytics);
    setAnalytics(allowAnalytics);
    setIsVisible(false);
    setShowSettings(false);
    if (allowAnalytics) {
      initGA();
    } else if (hadAnalytics) {
      // Consent withdrawn: reload so Google Analytics is no longer running
      window.location.reload();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.section
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-title"
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-50 inset-x-0 bottom-0 sm:inset-x-auto sm:left-6 sm:bottom-6 sm:w-[400px]
            bg-[#2A3A4A] text-white rounded-t-3xl sm:rounded-3xl
            shadow-[0_-8px_40px_rgba(16,24,32,0.35)] sm:shadow-[0_20px_60px_rgba(16,24,32,0.45)]
            ring-1 ring-white/10"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Grab handle: signals a bottom sheet on phones */}
          <div className="sm:hidden mx-auto mt-2.5 h-1 w-10 rounded-full bg-white/25" aria-hidden="true" />

          <div className="px-5 pt-4 pb-5 sm:p-6">
            <div className="flex items-start gap-4">
              <CookieMark animate={!reduceMotion} />
              <div className="min-w-0">
                <h2 id="cookie-title" className="text-lg font-semibold leading-snug text-white">
                  {showSettings ? 'Välj vilka cookies vi får använda' : 'Får vi räkna besöken?'}
                </h2>
                {!showSettings && (
                  <p className="mt-1.5 text-sm leading-relaxed text-white/80">
                    Med ditt godkännande använder vi Google Analytics för att se vilka sidor som hjälper
                    våra besökare. Inget används till annonser.
                  </p>
                )}
              </div>
            </div>

            <AnimatePresence initial={false} mode="wait">
              {!showSettings ? (
                <motion.div
                  key="simple"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => decide(false)}
                      className="min-h-12 rounded-xl px-3 text-sm font-semibold bg-white/10 ring-1 ring-white/25
                        hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC300]"
                    >
                      Bara nödvändiga
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(true)}
                      className="min-h-12 rounded-xl px-3 text-sm font-semibold bg-[#FFC300] text-[#2A3A4A]
                        hover:bg-[#ffcd33] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#2A3A4A]"
                    >
                      Tillåt statistik
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => setShowSettings(true)}
                      className="py-2 text-white/85 underline underline-offset-4 decoration-white/30 hover:decoration-[#FFC300] hover:text-white"
                    >
                      Välj själv
                    </button>
                    <Link
                      to="/cookies"
                      className="py-2 text-white/85 underline underline-offset-4 decoration-white/30 hover:decoration-[#FFC300] hover:text-white"
                    >
                      Läs mer om cookies
                    </Link>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <ul className="mt-4 divide-y divide-white/10 rounded-2xl bg-white/5 ring-1 ring-white/10">
                    <li className="flex items-start justify-between gap-4 p-4">
                      <div>
                        <p className="text-sm font-semibold">Nödvändiga</p>
                        <p className="mt-1 text-sm leading-relaxed text-white/75">
                          Behövs för att sajten ska fungera, till exempel för att komma ihåg ditt val här.
                          Alltid på.
                        </p>
                      </div>
                      <Switch checked disabled label="Nödvändiga cookies, alltid på" />
                    </li>
                    <li className="flex items-start justify-between gap-4 p-4">
                      <div>
                        <p className="text-sm font-semibold">Statistik</p>
                        <p className="mt-1 text-sm leading-relaxed text-white/75">
                          Google Analytics visar hur sajten används, till exempel vilka sidor som besöks.
                          Används inte till annonser.
                        </p>
                      </div>
                      <Switch checked={analytics} onChange={setAnalytics} label="Statistik med Google Analytics" />
                    </li>
                  </ul>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => (readConsent() ? setIsVisible(false) : setShowSettings(false))}
                      className="min-h-12 rounded-xl px-3 text-sm font-semibold bg-white/10 ring-1 ring-white/25
                        hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC300]"
                    >
                      {readConsent() ? 'Stäng' : 'Tillbaka'}
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(analytics)}
                      className="min-h-12 rounded-xl px-3 text-sm font-semibold bg-[#FFC300] text-[#2A3A4A]
                        hover:bg-[#ffcd33] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#2A3A4A]"
                    >
                      Spara val
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
